// packages
import React, { useCallback, useEffect, useState } from 'react';

import { IToggle } from '@unleash/nextjs';
import dynamic from 'next/dynamic';

import { useAppStore } from 'src/hooks/useAppStore';
import { CommerceData } from 'src/store/interface';
import { clevertapEventPush } from 'src/utils/analytics/clevertap/clevertapHelper';
import {
  commerceCTA,
  playerCtaEvent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import { clearCtaEventsProperty } from 'src/utils/analytics/mixpanel/mixpanelHelper';
import {
  deleteSessionStorage,
  getLocalStorage,
  getSessionStorage
} from 'src/utils/browserStorageHandler';
import { clearSubscriptionNudgePopupTimer } from 'src/utils/clearSubscriptionNudgePopupTimer';
import { checkIfEduauraa, getUsersAge } from 'src/utils/common';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  ASSET_SUBTYPE,
  DEVICE_TYPES,
  GUEST_TOKEN
} from 'src/utils/constants/AppConstants';
import {
  COMMERCE_WISHLISTED_PRODUCTS,
  PARTNER_NAME
} from 'src/utils/constants/localStorageConstants';
import { FeatureFlagConstants } from 'src/utils/constants/utilConstants';
import { getFeatureFlagConfig, isFeatureEnabled } from 'src/utils/RemoteConfig';
import {
  getAvodNudgeConfig,
  hideSubscriptionNudge,
  shouldSubscriptionNudgeDisplayed,
  showSubscriptionNudge
} from 'src/utils/subscriptionNudgeUtils';
import { triggerTeaserBanner } from 'src/utils/VideoPlayerUtils/Utils';

import {
  adjustShopIconContainer,
  adjustShopIconContainerOnChromecast,
  hideCommerceIcon,
  showCommerceIcon
} from './Commerce/commerceIconHelper';
import CommerceLandscapeLayout from './Commerce/CommerceLandscapeLayout';
import { showFreeMinutesGatingTimer } from './gatingTimer';
import {
  createEduauraaMessage,
  createScoreContainer,
  fetchNextVideos,
  fetchPreviousVideos,
  getSelfError,
  playNext,
  removeEduauraaMessage,
  removeScorecardWidget,
  toggleWatchTrailerButton,
  updateMandatoryRegistrationViewCount,
  watchHistoryApi
} from './playerCallbacks';
import {
  IBodyParamsWatchHistory,
  IrequestToPlayNextVideoProps
} from './PlayerInterface/PlayerCallbackInterface';
import {
  createSkipContentButton,
  requestToPlayNextVideo
} from './playerPopups';
import PlayerPopupsWrapper from './PlayerPopupsWrapper';
import { playerWrapper } from './PlayerStyles';
import { Commerce } from './utilConstants';
import { getAdStacks } from '../../../services/apiRequests/commerce/fetchAdStacks';
import { getBingAds } from '../../../services/apiRequests/commerce/fetchBingAds';
import { AdStacks } from '../../../services/responseTypes/commerceResponse';
import {
  TriggerPopupTypes,
  VideoPlayerPropTypes
} from '../../../types/videoplayer';
import { getPageName, mixpanelObject } from '../../../utils/analytics';
import {
  MIXPANEL_AUTO_PLAY,
  MIXPANEL_BUTTON_TYPE,
  MIXPANEL_CONTINUE_WATCHING,
  MIXPANEL_DEKEY,
  MIXPANEL_ELEMENT,
  MIXPANEL_IS_PREMIUM_CONTENT,
  MIXPANEL_NO,
  MIXPANEL_PARTNER_NAME,
  MIXPANEL_SHOP_PLAYER
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { getImageURL } from '../../../utils/ImageHelper/ImageURLGenerator';
import {
  deviceOrientation,
  getContentSpecification,
  getContentType,
  getTopCategory,
  isFullScreen
} from '../../../utils/playerHelper';
import {
  loadGoogleDAIScript,
  loadGoogleIMAScript
} from '../../../utils/scripts/playerScripts';
import { singlePlayApiUrlGenerator } from '../../../utils/UrlGenerator';
import PlayerWidgetSkeletonLoader from '../../playerWidgetSkeleton/playerWidgetSkeletonLoader';

let videoTagId = 'video-tag';
let isPopUpPlayer = false;

const SubscribeTeaserBannerWrapper = dynamic(
  () => import('./SubscribeTeaserBannerWrapper'),
  {
    loading: () => <PlayerWidgetSkeletonLoader />,
    ssr: true
  }
);

const getMixpanelData = (contentData: DynamicObject) => {
  const { assetDetails = {}, showDetails } = contentData;
  let assetInfo = showDetails || assetDetails;
  if (
    assetDetails &&
    (assetDetails.asset_subtype === 'live_event' ||
      assetDetails.asset_subtype === 'sports_vod')
  ) {
    assetInfo = assetDetails;
  }
  let playerAnalyticsObject: DynamicObject = {};
  if (
    window.playerAnalyticsObject &&
    Object.keys(window.playerAnalyticsObject).length
  ) {
    playerAnalyticsObject = window.playerAnalyticsObject;
    window.playerAnalyticsObject = {};
  }
  playerAnalyticsObject = {
    source: getPageName(window.location.pathname),
    pageName: getPageName(window.location.pathname),
    horizontalIndex: playerAnalyticsObject.horizontalIndex || 'N/A',
    verticalIndex: playerAnalyticsObject.verticalIndex || 'N/A',
    carouselName: playerAnalyticsObject.carouselName || 'N/A',
    carousalId: playerAnalyticsObject.carousalId || 'N/A',
    carouselIndex: playerAnalyticsObject.carouselIndex || 'N/A',
    tabName: playerAnalyticsObject.tabName || 'N/A',
    partnerName:
      mixpanelObject[MIXPANEL_PARTNER_NAME] === ''
        ? 'NA'
        : mixpanelObject[MIXPANEL_PARTNER_NAME] || 'N/A',
    origin: playerAnalyticsObject.origin || 'N/A',
    modelName: playerAnalyticsObject.modelName || 'N/A',
    clickId: playerAnalyticsObject.clickId || 'N/A',
    previewStatus: playerAnalyticsObject.previewStatus || 'N/A',
    dekey: mixpanelObject[MIXPANEL_DEKEY] || 'N/A',
    contentType:
      assetDetails && assetDetails.business_type
        ? assetDetails.business_type
        : 'Free',
    contentSpecification:
      assetDetails &&
      getContentSpecification(
        assetDetails.asset_type,
        assetDetails.asset_subtype
      ),
    topCategory: assetInfo && getTopCategory(assetInfo),
    isEduauraa: contentData && checkIfEduauraa(contentData),
    playerVersion: process.env.PLAYER_VERSION,
    appVersion: process.env.APP_VERSION || 'N/A'
  };

  return playerAnalyticsObject;
};

const VideoPlayer: React.FC<VideoPlayerPropTypes> = ({
  // onReady
  contentId,
  singlePlayDetails = {},
  primaryMediaId,
  /* Redux dependencies(not passed directly to player) required for callbacks - START */
  userData,
  countryList,
  similarFromReco,
  country,
  partnerSetting,
  setVITvod,
  displayLanguage,
  region,
  getSinglePlayDetails,
  contentLanguage,
  userType,
  plansMetaData,
  // isVmax,
  // tvodObj,
  callStaledApis,
  handleParentalPin,
  // isLoggedIn,
  // setBuyPlanPopupVisibleAction,
  setAvodPopupNudgeVisibleAction,
  // getPlans,
  // plans,
  // subscriptionPlansPending,
  // subscriptionPlansError,
  // z5Strings,
  getUpNextData,
  upNextApiState,
  isUpNextPending,
  // initialData,
  playInPopUp,
  // closeKeyMoments,
  // abrCapping,
  // isPartner,
  tvodState,
  toggleTeaserSubscriptionPopup,
  handleRentNowPopUp,
  tvodRentals,
  remoteConfig,
  handleCommerceLayout,
  /* Redux dependencies(not passed directly to player) required for callbacks - END */
  ...options // data from consumption page + redux dependencies - to be passed to player
}) => {
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const {
    appConfig,
    parentalData,
    setCompanionAdState,
    commerceData,
    commerceComponentData,
    usersData,
    setCommerceData,
    clearCommerceData,
    clearCommerceComponentData,
    setCommerceWishlistedProductsLength,
    setPageLoadPingUrl,
    upgradeData
  } = useAppStore();

  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [showTeaserBanner, setShowTeaserBanner] = useState(false);
  const [teaserData, setTeaserData] = useState({
    message: '',
    isAppInstall: false
  });
  const [playerPopupState, setplayerPopupState] = useState(false);
  const [activateCommerceLandscapeLayout, setActivateCommerceLandscapeLayout] =
    useState(false);
  const [popupProps, setPopupProps] = useState({} as TriggerPopupTypes);
  const [currentTimeValue, setCurrentTimeValue] = useState<number>(0);
  const [adStacksResponse, setAdStacksResponse] = useState<AdStacks>({
    timeStamps: [],
    videoReferenceId: ''
  });
  const [triggerCommerceLandscapeLayout, setTriggerCommerceLandscapeLayout] =
    useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const guestToken = getLocalStorage(GUEST_TOKEN);
  const commerceComponentDataLength = commerceComponentData?.length || 0;
  const orientationType = deviceOrientation();

  let netimage;
  if (playInPopUp) {
    videoTagId = 'video-tag-popup';
    isPopUpPlayer = true;
  } else {
    videoTagId = 'video-tag';
    isPopUpPlayer = false;
  }
  if (typeof document !== 'undefined') {
    netimage = document.createElement('img');
    netimage.src =
      'https://stcf-prod.zee5.com/prod/zee5Videojs/resources/images/group.svg';
  }

  /* Executes only once during assignment of Player object to playerRef */
  const attachPlayerCallbacks = () => {
    const player: any = playerRef?.current;
    player?.updateWrapperConfig({
      triggerPopUp: (props: TriggerPopupTypes) => {
        triggerPopUp({
          ...props
        });
      },
      updateMandatoryRegistrationViewCount: (props: any) => {
        updateMandatoryRegistrationViewCount({
          countryList,
          country,
          ...props
        });
      },
      getPosterUrl: (props: any) => {
        const posterUrl = getImageURL({
          ...props,
          deviceWidth: appConfig.deviceWidth
        });
        return posterUrl;
      },
      setVITvod: (props: any) => {
        setVITvod({
          ...props
        });
      },
      // getConcurrentUsers: async (props) => {
      //   const concurrentUsers = await getConcurrentUsers({
      //     ...props
      //   });
      //   return concurrentUsers;
      // },
      playNext: (props: any) => {
        playNext({
          country,
          region,
          ...props
        });
      },
      getSinglePlayDetails: (props: any) => {
        getSinglePlayDetails({
          ...props
        });
      },

      onClickCommerceIcon: () => {
        const checkDeviceOrientation = deviceOrientation();
        const fullScreenCheck = isFullScreen();
        commerceCTA(MIXPANEL_SHOP_PLAYER);
        if (
          checkDeviceOrientation === Commerce.MOBILE_LANDSCAPE ||
          (checkDeviceOrientation === Commerce.DESKTOP_LANDSCAPE &&
            fullScreenCheck)
        ) {
          setActivateCommerceLandscapeLayout(true);
        } else if (handleCommerceLayout) {
          handleCommerceLayout();
        }
      },

      currentTime: (currentTimeVal = 0) => {
        const currentTimeValue: number = Math.round(currentTimeVal);
        setCurrentTimeValue(currentTimeValue);
      },

      checkIsFullScreen: () => {
        const fullScreenCheck = isFullScreen();
        setIsFullscreen(fullScreenCheck);
      },

      isAdStacksDataAvailable: () => {
        return adStacksResponse?.timeStamps?.length > 0;
      },

      triggerForCommerceLandscape: () => {
        setTriggerCommerceLandscapeLayout(true);
      },

      getUpnextData: (props: any) => {
        getUpNextData({
          ...props
        });
      },
      fetchNextVideos: async (props: { contentId: string }) => {
        const getRecoData = await fetchNextVideos({
          displayLanguage,
          contentLanguage,
          country,
          userType,
          ...props
        });
        return getRecoData;
      },
      fetchPreviousVideos: async (props: any) => {
        // here props is playerref.
        const getPreviousRecoData = await fetchPreviousVideos({
          displayLanguage,
          country,
          ...props
        });
        return getPreviousRecoData;
      },
      watchHistoryApi: (props: {
        watchMethod: string;
        bodyParams: IBodyParamsWatchHistory;
      }) => {
        watchHistoryApi({
          userType,
          guestToken,
          ...props
        });
      },
      isSportsLiveEvent: (props: DynamicObject) => {
        //TO DO
        return false;
      },
      isFeatureEnabled: (key: string) => {
        return isFeatureEnabled(remoteConfig as IToggle[], key);
      },
      getFeatureFlagConfig: (key: string) => {
        return getFeatureFlagConfig(remoteConfig as IToggle[], key);
      },
      requestToPlayNextVideo: (props: IrequestToPlayNextVideoProps) => {
        requestToPlayNextVideo({
          country,
          playNext,
          ...props
        });
      },
      updateAdStatusCallback: (flag: boolean) => {
        if (!flag) {
          setCompanionAdState({
            isAdPlayingOnPlayer: flag,
            companionAdLoaded: false
          });
        } else {
          setCompanionAdState({ isAdPlayingOnPlayer: flag });
        }
      },
      singlePlayApiUrlGenerator: async (props: any) => {
        const url = await singlePlayApiUrlGenerator(
          {
            country,
            region,
            isVmax: false,
            translation: displayLanguage,
            languages: contentLanguage,
            gender: usersData?.gender,
            age: getUsersAge(usersData?.birthday),
            user: getSessionStorage(PARTNER_NAME),
            isParentalControlSet: false,
            ...props
          },
          remoteConfig
        );
        return url;
      },
      toggleWatchTrailerButton: (props: { disable: boolean }) => {
        toggleWatchTrailerButton({ ...props });
      },
      createEduauraaMessage: () => {
        createEduauraaMessage();
      },
      createScoreContainer: (props: any) => {
        createScoreContainer({ ...props });
      },
      removeScorecardWidget: () => {
        removeScorecardWidget();
      },
      removeEduauraaMessage: () => {
        removeEduauraaMessage();
      },
      clevertapEventPush: (props: any) => {
        const { eventName: event, mixpanelProperties: trackObject } = props;
        clevertapEventPush(event, trackObject);
      },
      callStaledApis: () => {
        if (typeof callStaledApis === 'function') {
          callStaledApis();
        }
      },
      createSkipContentButton: (props: any) => {
        createSkipContentButton(props);
      },
      showSubscriptionNudge: (props: any) => {
        showSubscriptionNudge({
          setAvodPopupNudgeVisibleAction,
          ...props
        });
      },
      hideSubscriptionNudge: (props: any) => {
        hideSubscriptionNudge({
          setAvodPopupNudgeVisibleAction,
          ...props
        });
      },
      triggerTeaserBanner: (props: any) => {
        setTeaserData(
          triggerTeaserBanner({
            ...props
          })
        );
        setShowTeaserBanner(true);
      },
      handleParentalPin: (props: any) => {
        handleParentalPin({ ...props });
      },
      // toggleSubscriptionPopup: (props) => {
      //   toggleSubscriptionPopup({ ...props });
      // },
      getAvodNudgeConfig: (props: any[]) => {
        return getAvodNudgeConfig(props);
      },
      clearSubscriptionNudgePopupTimer: () => {
        return clearSubscriptionNudgePopupTimer();
      },
      shouldSubscriptionNudgeDisplayed: (props: {
        playerRef: DynamicObject;
        nudgeType: string;
      }) => {
        return shouldSubscriptionNudgeDisplayed({ ...props });
      },
      removeSubscriptionNudge: () => {
        closePopUp();
        clearCtaEventsProperty(mixpanelObject);
        mixpanelObject[MIXPANEL_ELEMENT] = MIXPANEL_CONTINUE_WATCHING;
        mixpanelObject[MIXPANEL_BUTTON_TYPE] = MIXPANEL_AUTO_PLAY;
        mixpanelObject[MIXPANEL_IS_PREMIUM_CONTENT] = MIXPANEL_NO;
        playerCtaEvent(true);
      },
      selfErrorComponent: () => {
        getSelfError();
      },
      showFreeMinutesGatingTimer: (props: {
        playerRef: DynamicObject;
        allowedTime: string;
        gatingType: string;
      }) => {
        showFreeMinutesGatingTimer({
          ...props,
          bestOfferingPlan
        });
      }
    });
  };
  const triggerPopUp = (props: TriggerPopupTypes) => {
    setPopupProps(props);
    const isB2B = window.location.href.includes('b2b-consumption');
    if ((isB2B && props.name === 'networkRetry') || !isB2B) {
      const player: any = playerRef.current;
      if (player && player.videoPlayer != null) {
        player.videoPlayer.pause();
      }
      setplayerPopupState(true);
    }
  };

  const closePopUp = () => {
    const player: any = playerRef.current;
    setplayerPopupState(false);
    if (player && player.videoPlayer != null) {
      player.videoPlayer.play();
    }
  };
  const {
    usersPreferenceData,
    remoteConfig: rconfig,
    initialLaunchData,
    bestOfferingPlan
  } = useAppStore();

  useEffect(() => {
    // make sure Video.js player is only initialized once
    if (!playerRef.current) {
      const videoElement = videoRef.current;
      if (!videoElement) return;
      getPlayer();
    }
  }, [videoRef]);

  useEffect(() => {
    if (!playerLoaded) return;
    const player: DynamicObject | null = playerRef.current;
    if (
      player &&
      upgradeData &&
      typeof upgradeData === 'object' &&
      Object.keys(upgradeData).length > 0
    ) {
      (player as DynamicObject).updateWrapperConfig({
        upgradeData
      });
    }
  }, [upgradeData]);

  const getPlayer = () => {
    if (window.webPlayer) {
      import('../../player/videoJsPlayer')
        .then((Player) => {
          playerRef.current = Player.default;
          setPlayerLoaded(true);
        })
        .catch((e) => {
          e.message = `Player load issue : ${e.message}`;
          // trackErrors(e, {});
        });
    } else {
      setTimeout(getPlayer, 300);
    }
  };
  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    return () => {
      /**
       * While setting parental pin for the first time, it was observed that sometimes player instance is not there and hence it was throwing error.
       * checks has been added to resolve the same.
       */
      if (!playerLoaded) return;
      const player: any = playerRef.current;
      if (player && player.videoPlayer != null) {
        player.dispose();
      }
      playerRef.current = null;
    };
  }, [playerRef]);

  useEffect(() => {
    loadGoogleIMAScript();
    loadGoogleDAIScript();
    if (
      // contentId &&
      singlePlayDetails &&
      Object.keys(singlePlayDetails).length &&
      playerLoaded
    ) {
      const player: any = playerRef.current;
      // const contentId = '0-1-6z5281689';
      const { keyOsDetails: { sdrm: customdata = {}, nl = {} } = {} } =
        singlePlayDetails;
      const contentType = getContentType(singlePlayDetails.assetDetails);
      const mixpanelData = getMixpanelData(singlePlayDetails);
      // const videoUrl = getVideoUrl({
      //   browserType: getBrowserType(),
      //   data: singlePlayDetails
      // });
      // let xmins_free_config;
      // if (
      //   initialData?.countryList?.length &&
      //   initialData.countryList[0]?.xmins_free_config
      // ) {
      //   xmins_free_config = initialData.countryList[0].xmins_free_config;
      // }
      const displayLanguage = usersPreferenceData?.display_language;
      const licenseUrl = process.env.NEXT_PUBLIC_LICENSE_URL;
      const sos_abr_url = process.env.NEXT_SOS_ABR_URL;
      const playerScriptUrl = process.env.NEXT_PUBLIC_PLAYER_URL;
      const nimbus_api = process.env.NEXT_PUBLIC_NIMBUS_API;
      const ilt_tournament_id = process.env.NEXT_PUBLIC_ILT_TOURNAMENT_ID;
      const ilt_season_id = process.env.NEXT_PUBLIC_ILT_SEASON_ID;
      const pinScoreILT_flag =
        initialLaunchData?.country_config?.feature_flag?.ILT || false;
      let guestConfigCW = false;
      let guestConfigCWLS = '';
      let continueWatchingItemForGuest = '';
      const { enabled, payload } = getFeatureFlagConfig(
        rconfig,
        FeatureFlagConstants.CONFIG_CONTINUE_WATCHING_RAIL_FOR_GUEST_USER
      );

      if (enabled && payload) {
        guestConfigCW = enabled;
        guestConfigCWLS = payload['local_storage'];
        continueWatchingItemForGuest = payload['local_storage_total_items'];
      }

      /* Update contentDetails to player via setPlayerConfig method */
      player?.setPlayerConfig({
        contentId,
        primaryMediaId,
        singlePlayDetails,
        customdata,
        nl,
        contentType,
        displayLanguage,
        licenseUrl,
        mixpanelData,
        remoteConfig,
        guestConfigCW,
        guestConfigCWLS,
        continueWatchingItemForGuest,
        sos_abr_url,
        playerScriptUrl,
        nimbus_api,
        ilt_tournament_id,
        ilt_season_id,
        pinScoreILT_flag
      });

      // To remove any popups if present
      // removeAllPopus();

      /* Attach player callbacks so that player can utilize it */
      attachPlayerCallbacks();
      /* invoke player */
      closePopUp();
      player?.invokePlayer();

      // afterPlayCallbacks()
    }
  }, [contentId, singlePlayDetails, playerLoaded, displayLanguage]);

  // append redux props to options which are common to both callbacks & to pass to player
  const optionsString = JSON.stringify({
    ...options,
    userData: userData || {},
    partnerSetting,
    guestToken,
    country
  });

  // useEffect(() => {
  //   // get all subscription plans if not present
  //   if (
  //     !plans.length &&
  //     !subscriptionPlansPending &&
  //     !subscriptionPlansError &&
  //     contentId &&
  //     contentId !== ''
  //   ) {
  //     getSubscriptionPlans();
  //   }
  // });

  const getSpecialPlan = (
    plans: { isDefault: boolean }[] | null | undefined
  ) => {
    if (plans && plans?.length) {
      const attachplan = plans.find(
        (plan: { isDefault: boolean }) => plan.isDefault
      );
      return attachplan;
    }
  };

  useEffect(() => {
    const specialPlan = getSpecialPlan(plansMetaData);

    if (specialPlan && Object.keys(specialPlan).length) {
      (window as any).specialPlan = specialPlan;
    }
  }, [plansMetaData]);

  useEffect(() => {
    if (!playerLoaded) return;
    const player = playerRef.current;
    if (player) {
      (player as DynamicObject).updateUpnextApiState(
        upNextApiState,
        isUpNextPending
      );
    }
    // to be rechecked
  }, [upNextApiState, playerLoaded, isUpNextPending]);

  useEffect(() => {
    if (!playerLoaded) return;
    const player: DynamicObject | null = playerRef.current;
    const parsedOptions = JSON.parse(optionsString);
    /* Update additional data in player config passed as options from consumption page */
    if (player) {
      (player as DynamicObject).updateWrapperConfig({
        userData,
        tvodState,
        similarFromReco,
        ...parsedOptions
      });
    }
  }, [
    optionsString,
    tvodState,
    similarFromReco,
    playerLoaded,
    contentId,
    userData
  ]);

  useEffect(() => {
    if (!playerLoaded) return;
    const player: any = playerRef.current;
    if (player) {
      parentalData.showParentalPinPageFlag
        ? player?.videoPlayer?.pause()
        : player?.videoPlayer?.play();
    }
  }, [parentalData.showParentalPinPageFlag, playerLoaded]);

  const assetId = singlePlayDetails?.assetDetails?.id || '';
  const adStacksTimeStamps = adStacksResponse?.timeStamps || [];
  const adStacksVideoRefId = adStacksResponse?.videoReferenceId || '';

  const commerceFeatureFlag =
    (remoteConfig &&
      isFeatureEnabled(remoteConfig, FeatureFlagConstants.CONFIG_COMMERCE)) ||
    false;

  const removeCommerceContainerDOM = useCallback(() => {
    const sidebar = document.getElementById('commerceContainer');
    if (sidebar) {
      sidebar.remove();
    }
    setActivateCommerceLandscapeLayout(false);
  }, []);

  const getCommerceAds = useCallback(
    (timeStamp: string, videoReferenceId: string) => {
      if (assetId) {
        getBingAds({ videoReferenceId, timeStamp }).then(
          (commerceAdsResponse) => {
            const { bingAds = [], pageLoadPingUrl = '' } =
              commerceAdsResponse || {};
            if (bingAds?.length) {
              setCommerceData({ timeStamp, data: bingAds });
            }
            if (pageLoadPingUrl) {
              setPageLoadPingUrl(pageLoadPingUrl);
            }
          }
        );
      }
    },
    [adStacksVideoRefId, currentTimeValue]
  );

  // getAdStacks call for getting timestamps
  useEffect(() => {
    if (commerceFeatureFlag && country?.toLowerCase() === 'in') {
      if (assetId) {
        deleteSessionStorage(COMMERCE_WISHLISTED_PRODUCTS);
        clearCommerceData();
        clearCommerceComponentData();
      }
      if (
        !adStacksTimeStamps.length &&
        !adStacksVideoRefId &&
        assetId &&
        singlePlayDetails?.assetDetails?.asset_subtype === ASSET_SUBTYPE.MOVIE
      ) {
        getAdStacks(assetId).then((adStacksResponse) => {
          const { timeStamps = [], videoReferenceId = '' } =
            adStacksResponse || {};
          if (
            adStacksResponse &&
            Object.keys(adStacksResponse).length &&
            timeStamps?.length &&
            videoReferenceId
          ) {
            setAdStacksResponse(adStacksResponse);
          }
        });
      }
    }
  }, [country, commerceFeatureFlag, assetId]);

  // getBingAds call for getting products
  useEffect(() => {
    if (
      currentTimeValue &&
      adStacksTimeStamps.length &&
      adStacksVideoRefId &&
      adStacksTimeStamps.indexOf(currentTimeValue) > -1 &&
      // [10, 30].includes(currentTimeValue)
      !commerceData.find(
        (obj: CommerceData) => Number(obj?.timeStamp) === currentTimeValue
      )
    ) {
      getCommerceAds(currentTimeValue.toString(), adStacksVideoRefId);
    }
  }, [currentTimeValue, adStacksTimeStamps, adStacksVideoRefId]);

  useEffect(() => {
    if (
      commerceComponentDataLength &&
      (orientationType !== Commerce.MOBILE_PORTRAIT || !isFullscreen)
    ) {
      showCommerceIcon(playerRef?.current);
      detectChromeCast();
    }
  }, [commerceComponentDataLength, orientationType, isFullscreen]);

  /* eslint-disable cyclomatic-complexity/zee-codeBlockComplexity */
  useEffect(() => {
    if (triggerCommerceLandscapeLayout) {
      const productsWishlisted =
        getSessionStorage(COMMERCE_WISHLISTED_PRODUCTS) || [];
      setCommerceWishlistedProductsLength(productsWishlisted.length);
      setTriggerCommerceLandscapeLayout(false);
    }
    if (
      triggerCommerceLandscapeLayout &&
      activateCommerceLandscapeLayout &&
      (orientationType === Commerce.MOBILE_LANDSCAPE ||
        orientationType === Commerce.DESKTOP_LANDSCAPE)
    ) {
      setActivateCommerceLandscapeLayout(true);
    } else if (orientationType === Commerce.MOBILE_PORTRAIT) {
      if (isFullscreen) {
        hideCommerceIcon();
        if (activateCommerceLandscapeLayout) {
          removeCommerceContainerDOM();
        }
      } else if (commerceComponentDataLength) {
        showCommerceIcon(playerRef?.current);
        adjustShopIconContainer(true);
        removeCommerceContainerDOM();
      }
    } else if (
      orientationType === Commerce.MOBILE_LANDSCAPE &&
      commerceComponentDataLength
    ) {
      showCommerceIcon(playerRef?.current);
    }
    // In fullscreen mode, if commerce container is open and we exit fullscreen
    if (
      activateCommerceLandscapeLayout &&
      !isFullscreen &&
      orientationType === Commerce.DESKTOP_LANDSCAPE
    ) {
      adjustShopIconContainer(true);
      detectChromeCast();
      removeCommerceContainerDOM();
    }
  }, [
    triggerCommerceLandscapeLayout,
    isFullscreen,
    orientationType,
    activateCommerceLandscapeLayout
  ]);

  const detectChromeCast = () => {
    const isChromeCastAvailable = document.querySelector('.chromeCastIcon');
    if (isChromeCastAvailable) {
      adjustShopIconContainerOnChromecast(true);
    } else {
      adjustShopIconContainerOnChromecast(false);
    }
  };

  return (
    <div css={playerWrapper}>
      <div className="playerContentWrapper content">
        <div
          className={`playerContainer ${
            activateCommerceLandscapeLayout ? 'customPlayerContainer' : ''
          }`}>
          <video
            ref={videoRef}
            className={`playerComp content video-js ${
              isPopUpPlayer ? 'playerPopUp' : ''
            }`}
            id={videoTagId}
            autoPlay
            muted
            controls
            playsInline
          />
          {activateCommerceLandscapeLayout && (
            <CommerceLandscapeLayout
              onPlayer={true}
              closeLayout={removeCommerceContainerDOM}
              handleCommerceLayout={handleCommerceLayout}
            />
          )}
          {playerPopupState && (
            <PlayerPopupsWrapper
              playerRef={playerRef.current}
              countryList={countryList}
              popupProps={popupProps}
              closePopUp={closePopUp}
              toggleTeaserSubscriptionPopup={toggleTeaserSubscriptionPopup}
            />
          )}
        </div>
        {showTeaserBanner ? (
          <SubscribeTeaserBannerWrapper
            handleRentNowPopUp={() => handleRentNowPopUp(true)}
            data={singlePlayDetails && singlePlayDetails.assetDetails}
            toggleTeaserSubscriptionPopup={toggleTeaserSubscriptionPopup}
            tvodState={tvodState}
            teaserBannerText={teaserData?.message}
            isAppInstall={teaserData?.isAppInstall}
            playerRef={playerRef.current}
            tvodRentals={tvodRentals}
            commerceLandscapeLayoutState={activateCommerceLandscapeLayout}
          />
        ) : (
          appConfig.isRequestedByBot &&
          appConfig.actualDeviceType !== DEVICE_TYPES.MOBILE && (
            <PlayerWidgetSkeletonLoader />
          )
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
